import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { CyberPanel } from "@/components/ui/CyberPanel";
import { SafeImage } from "@/components/ui/SafeImage";
import type { PortfolioItem } from "@/features/portfolio-items/types";
import { getPortfolioTags } from "./portfolio-utils";
import { ImageLightbox } from "./ImageLightbox";

interface PortfolioDetailProps {
  item: PortfolioItem;
}

export function PortfolioDetail({ item }: PortfolioDetailProps) {
  const [activeImage, setActiveImage] = useState<number | null>(null);
  const tags = getPortfolioTags(item);

  return (
    <section className="mx-auto w-full max-w-7xl px-3 py-8 sm:px-6 sm:py-12 lg:px-8">
      <Button variant="ghost" asChild className="mb-6 w-full sm:w-auto">
        <Link to="/portfolio">Back to Portfolio</Link>
      </Button>

      <CyberPanel className="overflow-hidden p-0">
        <div className="grid min-w-0 gap-0 lg:grid-cols-[minmax(0,1.1fr)_minmax(0,0.9fr)]">
          <div className="bg-cyan-300/5">
            {item.images[0] ? (
              <button type="button" onClick={() => setActiveImage(0)} className="block w-full" aria-label={`Open ${item.title} image`}>
                <SafeImage src={item.images[0].url} alt={item.images[0].alt || item.title} fallbackLabel="No image" className="aspect-[16/10] w-full object-cover" />
              </button>
            ) : (
              <div className="flex aspect-[16/10] items-center justify-center text-sm uppercase tracking-[0.24em] text-cyan-100/70">
                No Image
              </div>
            )}
          </div>

          <div className="min-w-0 p-4 sm:p-8">
            <p className="text-xs font-semibold uppercase tracking-[0.28em] text-teal-200">
              {item.category?.name ?? "Portfolio"}
            </p>
            <h1 className="mt-3 break-words font-display text-3xl font-bold uppercase text-white sm:text-5xl">{item.title}</h1>
            <div className="mt-5 flex flex-wrap gap-2">
              {tags.map((tag) => (
                <span key={tag} className="rounded border border-cyan-300/30 bg-cyan-300/10 px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-cyan-100">
                  {tag}
                </span>
              ))}
            </div>
            <dl className="mt-6 grid gap-4 text-sm sm:grid-cols-2">
              {item.clientName ? (
                <div>
                  <dt className="uppercase tracking-[0.18em] text-content-muted">Client / Server</dt>
                  <dd className="mt-1 text-white">{item.clientName}</dd>
                </div>
              ) : null}
              {item.year ? (
                <div>
                  <dt className="uppercase tracking-[0.18em] text-content-muted">Project Date</dt>
                  <dd className="mt-1 text-white">{item.year}</dd>
                </div>
              ) : null}
            </dl>
            <p className="mt-6 whitespace-pre-line break-words leading-7 text-content-secondary sm:leading-8">
              {item.description ?? "Full project details are being prepared."}
            </p>
          </div>
        </div>
      </CyberPanel>

      {item.images.length > 1 ? (
        <div className="mt-8">
          <h2 className="font-display text-2xl font-bold uppercase text-white">Gallery</h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {item.images.map((image, index) => (
              <button
                key={image.id}
                type="button"
                onClick={() => setActiveImage(index)}
                aria-label={`Open ${image.alt || item.title} gallery image`}
                className="overflow-hidden rounded-lg border border-cyan-300/20 bg-black/50 transition hover:border-cyan-300/70 hover:shadow-neon"
              >
                <SafeImage src={image.url} alt={image.alt || item.title} fallbackLabel="No image" className="aspect-video w-full object-cover transition duration-500 hover:scale-105" loading="lazy" />
              </button>
            ))}
          </div>
        </div>
      ) : null}

      {activeImage !== null ? (
        <ImageLightbox
          images={item.images}
          activeIndex={activeImage}
          title={item.title}
          onClose={() => setActiveImage(null)}
          onChange={setActiveImage}
        />
      ) : null}
    </section>
  );
}
